import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { Box, Typography } from '@mui/material'
import { getLocalizedValueWithPrefix } from 'src/Libs/system-data-handler/utils/formatNumberWithSIPrefix'
import WallboxBiDiValues from './WallboxBiDiValues'
import { BoostModeSwitch, RightArrowButton, WallboxWidgetHeader } from '../shared'
import { isUndefined } from 'lodash'
import { DASHBOARD_WALLBOX_WIDGET_ID } from 'src/Libs/qa/ids'
import { ChargingStatus } from 'src/Routes/EMobility/EMobilityModels'
import { WIDGET_HEIGHT } from '../base-card/const'
import { useNavigate } from 'react-router-dom'
import ChargingProgressBar from 'src/Components/charging-progress-bar/ChargingProgressBar'
import ToggleChargingSessionStatusButton from './ToggleChargingSessionStatusButton'
import { isFlowTheme } from 'src/Libs/utils'

export interface WallboxWidgetProps {
  id: string
  title?: string
  wallboxType: number
  currentPowerPrice?: number
  isOffline?: boolean
  isChargingSessionPaused?: boolean
  toggleWallboxChargingStatus: () => Promise<void>
  isServerOffline?: boolean
  link?: string
  isDynamicPowerPriceEnabledForWallboxes: boolean
  chargingStatus?: ChargingStatus
  energyAll?: number
  energyDischarged?: number
  isSunModeActive: boolean | undefined
  isBoostModeActive: boolean | undefined
  hasSmartComponent: boolean | undefined
  wallboxStorageId: string | number
  wallboxIndex: number
}

interface StyleProps {
  hasLink: boolean
  isOffline: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  widgetContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  valueContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  value: {
    marginBottom: '-1px',
  },
  container: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.effectStyles.shadowMain,
    backgroundColor: theme.palette.background.paper,
    minHeight: WIDGET_HEIGHT,
    alignItems: 'stretch',
    minWidth: 200,
    flex: '1 1 200px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    cursor: props.hasLink && !props.isOffline ? 'pointer' : 'default',

    [theme.breakpoints.down('sm')]: {
      flexBasis: 'unset',
      minWidth: 'unset',
      minHeight: 'unset',
      height: '100%',
    },
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,

    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      justifyContent: 'flex-end',
    },
  },

  topRowWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1,
  },
}))

const getStateOfCharge = (energyAll: number | undefined, chargingStatus: ChargingStatus | undefined): number => {
  if (!energyAll) {
    return 0
  }

  if (energyAll > 100 || chargingStatus === 'WAIT') {
    return 100
  }

  return energyAll
}

const getFormattedEnergyValueAndUnit = (value: number): { value: string; unit: string } => {
  const { localizedValue, prefix } = getLocalizedValueWithPrefix(value, 1)

  return {
    value: localizedValue,
    unit: `${prefix}Wh`,
  }
}

/**
 * Data Card for wallbox used to be displayed on the dashboard
 *
 * TODO: to introduce sun mode look in git history. Relevant ticket is CPE-1876.
 */
const WallboxWidget: React.FC<WallboxWidgetProps> = ({
  id,
  title,
  currentPowerPrice,
  isOffline,
  isChargingSessionPaused,
  toggleWallboxChargingStatus,
  isServerOffline,
  link,
  isDynamicPowerPriceEnabledForWallboxes,
  wallboxType,
  chargingStatus,
  energyAll,
  energyDischarged,
  isSunModeActive,
  isBoostModeActive,
  hasSmartComponent,
  wallboxIndex,
  wallboxStorageId,
}) => {
  const stateOfCharge = getStateOfCharge(energyAll, chargingStatus)
  const isCharging = chargingStatus === 'CHARGING'
  const isDischarging = chargingStatus === 'DISCHARGING'
  const { classes } = useStyles({ hasLink: !!link, isOffline: isOffline ?? false })
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const isBiDi = wallboxType === 5
  const isStandby = chargingStatus === 'STANDBY'
  const showToggleChargingSessionStatusButton = !isBiDi && !isUndefined(chargingStatus)
  const showDynamicPricesIcon = currentPowerPrice !== undefined && isDynamicPowerPriceEnabledForWallboxes && energyAll !== undefined
  const isShowBiDiValues = isBiDi && (isCharging || isDischarging || isStandby)
  const isShowNoVehicle = energyAll === undefined

  const isStopped = chargingStatus !== 'CHARGING' && chargingStatus !== 'DISCHARGING'

  const getRenderValue = (): string | JSX.Element => {
    if (isShowBiDiValues) {
      return <WallboxBiDiValues {...{ energyAll, energyDischarged, formatter: getFormattedEnergyValueAndUnit }} />
    }

    if (isShowNoVehicle) {
      return <Typography variant="caption">{t('dashboard.liveView.widgets.noVehicle')}</Typography>
    }

    const { value, unit } = getFormattedEnergyValueAndUnit(energyAll ?? 0)

    return (
      <Typography variant="h1" className={classes.value}>
        {value}
        <Typography variant="h2" component="span">
          {` ${unit}`}
        </Typography>
      </Typography>
    )
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (link && isOffline !== true) {
      event.preventDefault()
      navigate(link)
    }
  }

  return (
    <Box className={classes.container} data-qa={`${DASHBOARD_WALLBOX_WIDGET_ID}-${id}`} onClick={handleClick}>
      <Box className={classes.topRowWrapper}>
        <WallboxWidgetHeader
          title={title}
          isOffline={isOffline}
          isChargingSessionPaused={isChargingSessionPaused}
          chargingStatus={chargingStatus}
          energyAll={energyAll}
          showDynamicPricesIcon={showDynamicPricesIcon}
          isDynamicPowerPriceEnabledForWallboxes={isDynamicPowerPriceEnabledForWallboxes}
          isCharging={isCharging}
          isPriceBelowLimit={true}
        />
        <Box className={classes.controls}>
          <BoostModeSwitch
            isOffline={isOffline}
            isServerOffline={isServerOffline}
            isSunModeActive={isSunModeActive}
            isBoostModeActive={isBoostModeActive}
            hasSmartComponent={hasSmartComponent}
            wallboxIndex={wallboxIndex}
            wallboxStorageId={wallboxStorageId}
            isActiveChargingSession={chargingStatus !== undefined}
            isNoVehicleConnected={energyAll === undefined}
            isFlowEVChargingStrategyBoostMode={!isSunModeActive}
          />

          {link && <RightArrowButton link={link} />}
        </Box>
      </Box>
      {!isOffline && (
        <Box>
          <Box className={classes.widgetContainer}>
            <Box className={classes.valueContainer}>{getRenderValue()}</Box>
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <ChargingProgressBar
          disabled={isOffline}
          discharging={isDischarging}
          currentChargeInPercentage={stateOfCharge}
          stopped={isChargingSessionPaused || isStopped}
        />
        {showToggleChargingSessionStatusButton && !isFlowTheme && (
          <ToggleChargingSessionStatusButton
            chargingStatus={chargingStatus}
            isChargingSessionPaused={isChargingSessionPaused}
            toggleWallboxChargingStatus={toggleWallboxChargingStatus}
          />
        )}
      </Box>
    </Box>
  )
}

export default WallboxWidget
